"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { ArrowRight, ChevronDown, FileText, Menu, Phone } from "lucide-react";

type LinkItem = { label: string; href: string };

const secureDestructionLinks: LinkItem[] = [
  { label: "Hard Drive", href: "/hard-drives" },
  { label: "Paper", href: "/paper" },
  { label: "Products & Electronics", href: "/electronics-media-products" },
  { label: "Hazardous Waste", href: "/hazardous-waste" },
  { label: "IP & Product Destruction", href: "/ip-destruction" },
];

const primaryLinksBeforeDropdown: LinkItem[] = [
  { label: "ITAD", href: "/itad" },
];

const primaryLinksAfterDropdown: LinkItem[] = [
  { label: "Recycling", href: "/recycling" },
  { label: "About Us", href: "/about-us" },
  { label: "How it Works", href: "/how-it-works" },
  { label: "Blogs", href: "/blogs" },
  { label: "Contact Us", href: "/contact-us" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-background/95 shadow-[0_1px_0_0_hsl(var(--border))] backdrop-blur-lg"
          : "bg-background"
      }`}
    >
      <div className="container flex h-16 items-center justify-between px-4">
        <Link href="/" className="mr-2 flex min-w-0 shrink items-center gap-2 group sm:mr-4" aria-label="Viking Shred — home">
          <img src="/images/logo.webp" alt="Viking Shred" width={234} height={32} className="h-auto w-[132px] max-w-full select-none min-[360px]:w-[160px] min-[400px]:w-[190px] sm:w-[234px]" />
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-1 lg:flex xl:gap-3" aria-label="Primary navigation">
          {primaryLinksBeforeDropdown.map((link) => (
              <Link key={link.href} href={link.href} className="link-underline whitespace-nowrap px-0.5 py-2 text-[12.5px] font-medium text-foreground/85 transition-colors hover:text-primary xl:px-1">
              {link.label}
            </Link>
          ))}
          <DesktopDropdown label="Secure Destruction" links={secureDestructionLinks} />
          {primaryLinksAfterDropdown.map((link) => (
              <Link key={link.href} href={link.href} className="link-underline whitespace-nowrap px-0.5 py-2 text-[12.5px] font-medium text-foreground/85 transition-colors hover:text-primary xl:px-1">
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-2 lg:flex xl:gap-4">
          <Button asChild className="btn-diamond-primary flex h-10 items-center gap-2 rounded-md px-3 text-[12px] font-bold uppercase tracking-wide xl:px-5">
            <Link href="/instant-quote">
              <FileText className="h-4 w-4" />
              <span>INSTANT QUOTE</span>
            </Link>
          </Button>
          <a href="tel:866-261-5838" className="flex items-center gap-2 whitespace-nowrap text-[12px] font-extrabold text-primary transition-colors hover:text-accent" aria-label="Call Viking Shred at 866-261-5838">
            <Phone className="h-4 w-4" />
            <span className="hidden xl:inline">(866)261-5838</span>
          </a>
        </div>

        <div className="flex shrink-0 items-center gap-2 lg:hidden">
          <a href="tel:866-261-5838" className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary" aria-label="Call Viking Shred">
            <Phone className="h-4 w-4" />
          </a>
          <Button asChild size="sm" className="btn-diamond-primary h-10 w-10 px-0 text-xs min-[480px]:w-auto min-[480px]:px-3">
            <Link href="/instant-quote" aria-label="Instant quote">
              <FileText className="h-4 w-4 min-[480px]:hidden" />
              <span className="hidden min-[480px]:inline">INSTANT QUOTE</span>
            </Link>
          </Button>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10" aria-label="Open navigation menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88%] max-w-sm p-0">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="flex h-full flex-col">
                <div className="border-b px-6 pb-5 pt-6">
                  <img src="/images/logo.webp" alt="Viking Shred" width={206} height={28} className="h-7 w-auto select-none" />
                </div>
                <nav className="flex-1 overflow-y-auto px-6 py-4" aria-label="Mobile navigation">
                  {primaryLinksBeforeDropdown.map((link) => <MobileLink key={link.href} link={link} close={() => setMobileOpen(false)} />)}
                  <details className="border-b border-border/60 py-1">
                    <summary className="flex cursor-pointer list-none items-center justify-between py-3 text-[15px] font-medium text-foreground">
                      Secure Destruction
                      <ChevronDown className="h-4 w-4 opacity-50" />
                    </summary>
                    <div className="space-y-1 pb-3 pl-3">
                      {secureDestructionLinks.map((link) => <MobileLink key={link.href} link={link} close={() => setMobileOpen(false)} />)}
                    </div>
                  </details>
                  {primaryLinksAfterDropdown.map((link) => <MobileLink key={link.href} link={link} close={() => setMobileOpen(false)} />)}
                </nav>
                <div className="space-y-3 border-t bg-secondary/40 p-6">
                  <Button asChild className="btn-diamond-primary h-12 w-full text-sm font-bold">
                    <Link href="/instant-quote" onClick={() => setMobileOpen(false)}>
                      <FileText className="mr-2 h-4 w-4" />
                      INSTANT QUOTE
                    </Link>
                  </Button>
                  <a href="tel:866-261-5838" className="flex items-center justify-center gap-2 font-extrabold text-primary">
                    <Phone className="h-4 w-4" />
                    (866) 261-5838
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function DesktopDropdown({ label, links }: { label: string; links: LinkItem[] }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        className="link-underline flex items-center gap-1 whitespace-nowrap px-1 py-2 text-[12.5px] font-medium text-foreground/85 transition-colors hover:text-primary"
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 opacity-50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-2 w-72 rounded-xl border border-border bg-card p-2 shadow-xl">
          {links.map((link) => (
            <Link key={link.href} href={link.href} onClick={() => setOpen(false)} className="flex items-center justify-between rounded-lg px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-secondary hover:text-primary">
              {link.label}
              <ArrowRight className="h-3.5 w-3.5 opacity-40" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileLink({ link, close }: { link: LinkItem; close: () => void }) {
  return (
    <Link href={link.href} onClick={close} className="flex items-center justify-between border-b border-border/60 py-3 text-[15px] font-medium text-foreground transition-colors hover:text-primary">
      {link.label}
      <ArrowRight className="h-4 w-4 opacity-40" />
    </Link>
  );
}
